import { uiText } from "@/lib/i18n/uiText";
import { Link } from "@/lib/router-compat";
import { AlertTriangle } from "lucide-react";
import { XIcon, LinkedInIcon, InstagramIcon } from "@/components/brand/SocialIcons";
import { useTranslation } from "react-i18next";
import { SITE_NAME } from "@/lib/entities";
import { Logo } from "@/components/brand/Logo";
import { openConsentSettings } from "@/lib/consent";

interface FooterLink {
  label: string;
  to: string;
}

const COLUMNS: { heading: string; links: FooterLink[] }[] = [
  {
    heading: "Predictions",
    links: [
      { label: "Today's Predictions", to: "/predictions/today" },
      { label: "AI Predictions", to: "/ai-predictions" },
      { label: "High Odds", to: "/high-odds" },
      { label: "Safe Bets", to: "/safe-bets" },
      { label: "Bonus Picks", to: "/predictions/bonus-picks-today" },
      { label: "Predictions Nigeria", to: "/predictions/nigeria-today" },
      { label: "All Predictions", to: "/predictions" },
    ],
  },
  {
    heading: "Sports",
    links: [
      { label: "Leagues", to: "/leagues" },
      { label: "Competitions", to: "/competitions" },
      { label: "Countries", to: "/countries" },
      { label: "US Sports", to: "/nfl-predictions" },
      { label: "Betting Markets", to: "/markets" },
      { label: "Historical Odds", to: "/historical-odds" },
    ],
  },
  {
    heading: "Tools",
    links: [
      { label: "Match Analysis", to: "/analysis" },
      { label: "Track Record", to: "/track-record" },
      { label: "Fixtures Archive", to: "/fixtures" },
      { label: "Performance", to: "/performance" },
      { label: "Betting Guides", to: "/guides" },
      { label: "Blog", to: "/blog" },
    ],
  },
  {
    heading: "Company",
    links: [
      { label: "About", to: "/about" },
      { label: "How Our AI Works", to: "/how-it-works" },
      { label: "Contact", to: "/contact" },
      { label: "AI Access", to: "/ai-access" },
      { label: "Agent API (MCP)", to: "/docs/mcp" },
    ],
  },
  {
    heading: "Legal",
    links: [
      { label: "FAQ", to: "/faq" },
      { label: "Privacy Policy", to: "/privacy" },
      { label: "Terms of Service", to: "/terms" },
      { label: "Responsible Gambling", to: "/responsible-gambling" },
      { label: "Site Map", to: "/sitemap" },
    ],
  },
];

export function SiteFooter() {
  const year = new Date().getFullYear();
  const { t } = useTranslation();
  return (
    <footer className="border-t border-border bg-card/30 px-4 py-10 sm:px-6">
      <div className="container mx-auto max-w-6xl">
        <div className="grid grid-cols-2 gap-8 sm:grid-cols-3 lg:grid-cols-6">
          <div className="col-span-2 space-y-2 sm:col-span-3 lg:col-span-1">
            <Logo variant="full" to="/" imgClassName="h-9" />
            <p className="text-xs text-muted-foreground">{t("footer.tagline")}</p>
            <div className="flex items-center gap-3">
              <a
                href="https://x.com/statlign"
                target="_blank"
                rel="noopener noreferrer"
                aria-label={uiText("Statlign on X")}
                className="inline-flex text-muted-foreground transition-colors hover:text-primary"
              >
                <XIcon className="h-4 w-4" />
              </a>
              <a
                href="https://www.linkedin.com/company/statlign"
                target="_blank"
                rel="noopener noreferrer"
                aria-label={uiText("Statlign on LinkedIn")}
                className="inline-flex text-muted-foreground transition-colors hover:text-primary"
              >
                <LinkedInIcon className="h-4 w-4" />
              </a>
              <a
                href="https://www.instagram.com/statlign/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label={uiText("Statlign on Instagram")}
                className="inline-flex text-muted-foreground transition-colors hover:text-primary"
              >
                <InstagramIcon className="h-4 w-4" />
              </a>
            </div>
          </div>

          {COLUMNS.map((col) => (
            <nav key={col.heading} aria-label={uiText(col.heading)} className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-foreground">
                {uiText(col.heading)}
              </p>
              <ul className="space-y-1.5">
                {col.links.map((link) => (
                  <li key={link.to}>
                    <Link
                      to={link.to}
                      className="text-sm text-muted-foreground transition-colors hover:text-primary"
                    >
                      {uiText(link.label)}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
          ))}
        </div>

        <div className="mt-8 space-y-3 border-t border-border pt-6">
          <div className="flex items-start gap-2.5 rounded-lg bg-muted/30 p-3">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
            <p className="text-xs text-muted-foreground">
              {t("footer.description")}{" "}
              <a
                href="https://www.begambleaware.org"
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-primary underline underline-offset-2 hover:opacity-80"
              >
                BeGambleAware.org
              </a>
              .
            </p>
          </div>
          <div className="flex flex-col items-center gap-2 sm:flex-row sm:justify-center">
            <p className="text-center text-xs text-muted-foreground">
              © {year} {SITE_NAME}, by SYNETAIROS CONCEPTS. {t("footer.rights")}
            </p>
            <span aria-hidden="true" className="hidden text-xs text-muted-foreground sm:inline">
              ·
            </span>
            <button
              type="button"
              onClick={openConsentSettings}
              className="text-xs text-muted-foreground underline underline-offset-2 transition-colors hover:text-primary"
            >
              {t("footer.cookieSettings", "Cookie settings")}
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
}
